import { useEffect, useRef, useState } from 'react';
import { API_ORIGIN } from '@/app/constants';
import { useCredits, useFetch, useFetchStream, useTrials } from '@/app/hooks';
import { useStore } from '@/app/providers/store';
import type { TaskInfo } from '@/app/types';
import { tasksGet } from '../apis';
import { FINISHED_STATES, MIN_HINT_WAIT_MINUTES, TASK_STATE_URL } from '../constants';
import type { DisplayTaskState, TaskStateInfo } from '../types';
import { removeProgressStorage } from './use-task-progress';

const convertState = (state: TaskStateInfo['state']): DisplayTaskState => {
  // 后端的这些进入 processing 前的状态，前端都显示 "排队中"
  if (state === 'created' || state === 'queueing' || state === 'preparation' || state === 'scheduling') {
    return 'queueing';
  }

  // 后端状态为 unspecified 时，前端显示 failed
  if (state === 'unspecified') {
    return 'failed';
  }

  // 其他状态，前端和后端一致
  return state;
};

interface UseTaskStateOptions {
  onTaskUpdate?: (taskId: string, task: Partial<TaskInfo>) => void;
}

export const useTaskState = (task: TaskInfo, options?: UseTaskStateOptions) => {
  const { updateTask: storeUpdateTask, invalidateUserQuota } = useStore((state) => ({
    updateTask: state.updateTask,
    invalidateUserQuota: state.invalidateUserQuota,
  }));
  const updateTask = options?.onTaskUpdate ?? storeUpdateTask;
  const { updateTrials } = useTrials();
  const { updateCredits } = useCredits();

  const [taskState, setTaskState] = useState<DisplayTaskState>(convertState(task.state));
  const [queueWaitTime, setQueueWaitTime] = useState<TaskStateInfo['queue_wait_time']>();
  const [isWaitTimeProlonged, setIsWaitTimeProlonged] = useState(false);
  const prevMaxWait = useRef<number | undefined>(undefined);

  // 用于标记是否需要保持 SSE 连接（获取最新的 task state）
  const shouldKeepSSERef = useRef(false);

  const { loading, run: getTaskDetail } = useFetch(
    (id: string) =>
      tasksGet({
        path: {
          id,
        },
      }),
    {
      manual: true,
      onSuccess: ({ data }) => {
        if (!data) {
          setTaskState('failed');
          removeProgressStorage(task.id);
          updateCredits();
          updateTrials();
          invalidateUserQuota();
          return;
        }

        removeProgressStorage(task.id);
        setTaskState(convertState(data.state));
        updateTask(data.id, data);
      },
    },
  );

  // 更新任务状态
  const updateTaskState = (id: string, { state, err_code }: TaskStateInfo) => {
    if (state === 'success') {
      shouldKeepSSERef.current = false;
      setTaskState('fetchingTaskDetail');
      // 成功后获取 task 详情,详情返回后会通过 SSE onSuccess 更新完整数据
      !loading && getTaskDetail(id);
      return;
    }

    // 除了 success 状态，其他状态都更新
    setTaskState(convertState(state));
    updateTask(id, {
      state: state,
      err_code,
    });

    if (state === 'failed') {
      shouldKeepSSERef.current = false;
      removeProgressStorage(task.id);
      updateCredits();
      updateTrials();
      invalidateUserQuota();
      return;
    }

    if (state === 'canceled') {
      shouldKeepSSERef.current = false;
      updateCredits();
      updateTrials();
      invalidateUserQuota();
      return;
    }
    // 其他状态，保持 SSE 连接
    shouldKeepSSERef.current = true;
  };

  // SSE 获取任务状态
  const { run: getTaskState, close: closeGetTaskState } = useFetchStream<{ id: string }>(TASK_STATE_URL, {
    baseURL: API_ORIGIN,
    onMessage: (str) => {
      const dataObjects = str
        .split('data:')
        .map((d) => d.trim())
        .filter((d) => d);
      for (const dataString of dataObjects) {
        try {
          const json = JSON.parse(dataString);
          if (json.state && json.state !== task.state) {
            updateTaskState(task.id, json);
          }
          if (!FINISHED_STATES.includes(json.state) && json.queue_wait_time) {
            setQueueWaitTime(json.queue_wait_time);

            // 判断是否需要增加额外提示（业务需求：等待区间变化 & 等待时长增加，且最新等待时间大于等于 MIN_HINT_WAIT_MINUTES 分钟时，增加额外提示）
            const minWait = json.queue_wait_time.min[0];
            const maxWait = json.queue_wait_time.max[0];
            const isWaitTimeProlonged =
              prevMaxWait.current !== undefined && minWait >= prevMaxWait.current && minWait >= MIN_HINT_WAIT_MINUTES;
            setIsWaitTimeProlonged(isWaitTimeProlonged);
            prevMaxWait.current = maxWait;
          }
        } catch (error) {
          console.error('Error_from_get_task_state: ', error);
        }
      }
    },
    onClose: () => {
      // 如果任务状态还没变成 success/failed，可能是异常原因断开了，需要重新连接
      if (shouldKeepSSERef.current) {
        getTaskState({ id: task.id });
      }
    },
  });

  const handleCancelTask = (id: string) => {
    // 刷新任务状态
    getTaskDetail(id);

    // 刷新积分和试用次数
    updateCredits();
    updateTrials();
    invalidateUserQuota();

    // 关闭 SSE 连接
    shouldKeepSSERef.current = false;
    closeGetTaskState();
  };

  useEffect(() => {
    if (FINISHED_STATES.includes(task.state)) {
      // 完成或失败状态，删除进度条的 localStorage 记录
      removeProgressStorage(task.id);
    } else {
      getTaskState({ id: task.id });
    }
  }, []);

  return { taskState, queueWaitTime, handleCancelTask, isWaitTimeProlonged };
};
